/**
 * @description Hook for managing agent configuration
 * This abstracts the common pattern of handling agent configuration including
 * finding agent, managing config state, and handling update operations for existing agent instances
 */

import { useState, useEffect } from 'react';
import { useUpdateAgentInstance } from '@/hooks/useAgentInstance';
import { useMarketplaceAgents } from '@/hooks/useMarketplaceAgents';
import { useAppStore } from '@/store/appStore';
import { AgentState } from '@/constants/agentStates';
import { toast } from '@strivelabs/ui-core';
import { log } from '@/lib/logger';

interface AgentConfigurationCallbacks {
  onSuccess?: (isUpdate: boolean) => void;
  onError?: (error: Error | unknown, isUpdate: boolean) => void;
  onStart?: (isUpdate: boolean) => void;
  onConfigurationSaved?: () => void; // New callback for when config is saved
}

interface AgentConfigurationOptions {
  agentShortName: string;
  defaultConfig?: Record<string, unknown>;
  callbacks?: AgentConfigurationCallbacks;
  successMessages?: {
    create?: string;
    update?: string;
  };
  errorMessages?: {
    agentNotFound?: string;
    createFailed?: string;
    updateFailed?: string;
  };
  autoManageState?: boolean;
}

export const useAgentConfiguration = ({
  agentShortName,
  defaultConfig = {},
  callbacks = {},
  successMessages = {},
  errorMessages = {},
  autoManageState = true
}: AgentConfigurationOptions) => {
  const [config, setConfig] = useState(defaultConfig);
  const [isInitialized, setIsInitialized] = useState(false);

  // Platform hooks
  const { data: marketplaceResponse } = useMarketplaceAgents();
  const updateAgentInstance = useUpdateAgentInstance();
  const setCurrentAgentState = useAppStore(state => state.setCurrentAgentState);
  const agentInstances = useAppStore(state => state.agentInstances);

  // Default messages
  const defaultSuccessMessages = {
    update: `${agentShortName} agent configuration updated successfully`,
    ...successMessages
  };

  const defaultErrorMessages = {
    agentNotFound: `${agentShortName} agent not found in marketplace`,
    updateFailed: "Failed to update agent configuration. Please try again.",
    ...errorMessages
  };

  // Find the agent from marketplace
  const targetAgent = marketplaceResponse?.agents?.find(agent => 
    agent.short_name === agentShortName
  );

  // Find existing agent instance
  const existingInstance = agentInstances?.find(instance => 
    instance.agent_id === targetAgent?.id
  );

  // Initialize config with existing instance data
  useEffect(() => {
    if (existingInstance?.config && !isInitialized) {
      log('info', 'Initializing config from existing instance', { config: existingInstance.config });
      setConfig(existingInstance.config);
      setIsInitialized(true);
    } else if (!existingInstance && !isInitialized && marketplaceResponse) {
      log('info', 'Initializing config with default values', { defaultConfig });
      setConfig(defaultConfig);
      setIsInitialized(true);
    }
  }, [existingInstance, defaultConfig, isInitialized, marketplaceResponse]);

  // Configure agent (update only)
  const configure = async (configData?: Record<string, unknown>) => {
    if (!targetAgent) {
      const error = new Error(defaultErrorMessages.agentNotFound);
      log('error', 'Agent not found in marketplace', { agentShortName });
      
      if (callbacks.onError) {
        callbacks.onError(error, false);
      } else {
        toast({
          title: "Error",
          description: defaultErrorMessages.agentNotFound,
          variant: "destructive",
        });
      }
      return;
    }

    if (!existingInstance?.id) {
      const error = new Error('Agent instance not found. Please install the agent first.');
      log('error', 'Trying to configure agent without existing instance', { agentShortName });
      
      if (callbacks.onError) {
        callbacks.onError(error, false);
      } else {
        toast({
          title: "Error",
          description: 'Agent instance not found. Please install the agent first.',
          variant: "destructive",
        });
      }
      return;
    }

    const finalConfig = configData || config;

    try {
      if (callbacks.onStart) {
        callbacks.onStart(true);
      }

      // Update existing agent instance
      await updateAgentInstance.mutateAsync({
        agentInstanceId: existingInstance.id,
        payload: {
          agent_instance: {
            config: finalConfig
          }
        }
      });
      
      log('info', 'Agent configuration updated', { agentShortName, config: finalConfig });

      // Update local config state to reflect the saved data
      setConfig(finalConfig);

      // Set the agent state to idle state
      if (autoManageState) {
        setCurrentAgentState(AgentState.Idle);
      }

      if (callbacks.onSuccess) {
        callbacks.onSuccess(true);
      } else {
        toast({
          title: "Configuration Updated!",
          variant: "success",
          description: defaultSuccessMessages.update,
        });
      }

      // Call the configuration saved callback to switch tabs
      if (callbacks.onConfigurationSaved) {
        callbacks.onConfigurationSaved();
      }
    } catch (error) {
      log('error', 'Error updating agent configuration', { error, agentShortName });
      
      if (callbacks.onError) {
        callbacks.onError(error, true);
      } else {
        toast({
          title: "Error",
          description: defaultErrorMessages.updateFailed,
          variant: "destructive",
        });
      }
    }
  };

  // Reset config to default
  const resetConfig = (newDefaultConfig?: Record<string, unknown>) => {
    const resetTo = newDefaultConfig || defaultConfig;
    setConfig(resetTo);
    log('info', 'Config reset to default', { agentShortName, config: resetTo });
  };

  // Update specific config field
  const updateConfigField = (field: string, value: unknown) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update entire config
  const updateConfig = (newConfig: Record<string, unknown>) => {
    setConfig(newConfig);
  };

  return {
    // State
    config,
    isInitialized,
    
    // Agent info
    targetAgent,
    existingInstance,
    isExisting: Boolean(existingInstance?.id),
    
    // Actions
    configure,
    resetConfig,
    updateConfigField,
    updateConfig,
    
    // Loading states
    isUpdating: updateAgentInstance.isPending,
    isLoading: updateAgentInstance.isPending,
    
    // Computed values
    hasChanges: JSON.stringify(config) !== JSON.stringify(existingInstance?.config || defaultConfig)
  };
};